import React, { useCallback, useMemo } from "react";
import classNames from "classnames";
import { get } from "lodash";
import { FieldArray } from "react-final-form-arrays";
import { useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { useForm, useFormState } from "react-final-form";
import { Collapse } from "@blueprintjs/core";
import { isNotEmpty } from "utils/validate";
import { EDIT_RS_WORKSPACE_MESSAGES, InsightLabels } from "shared/labels";
import RemoveConfirmDialog from "rs-components/RemoveConfirmDialog";
import Input from "rs-components/Input";
import SelectInput from "rs-components/Input/components/SelectInput";
import SelectItemRenderer from "rs-components/Input/components/SelectInput/components/SelectItemRenderer";
import ScoreIcon from "rs-components/ScoreIcon";
import SimpleRemoveButton from "components/SimpleRemoveButton";
import { InsightFormFields } from "../..";
import IconButton from "components/IconButton";
import HtmlText from "components/HtmlText";
import { processVariablesSelector } from "store/global/global.selectors";
import { incompleteSimulationsIdsSelector, insightDefaultNameSelector, insightExpandedInputSetIndexSelector, insightInputSetInputOptionsSelector, persistedInsightsDataSelector, previewRequestsSelector, workspaceCellModelsSelector, workspaceCurrentInsightIdSelector, workspaceOptimizationsSelector, workspaceRecipesSelector, workspaceSimulationsSelector, } from "store/rs-workspace/selectors/rs-workspace.selectors";
import { getResultSetsSelector } from "store/rs-workspace/selectors/results.selectors";
import { deleteTargetSelector, isEditableSelector, } from "store/knowledge-base-item/knowledge-base-item.selectors";
import { getInsightScore, removeInsightOutputScores, setInsightExpandedInputSet, } from "store/rs-workspace/actions/rs-workspace.actions";
import { setDeleteTarget } from "store/knowledge-base-item/knowledge-base-item.actions";
import { INSIGHT_INPUT_COLORS } from "store/rs-workspace/utils/scenario.utils";
import { calculateInputSetFinalScore, checkBasicScoreCalculationConditions, checkCanDisplayScore, checkInputSetUsed, getInsightDialogSubmitPayload, getInsightInputVariantsOptions, getInsightLatestScoreData, getInsightPreviewOperationPending, InsightDialogTestIds, prepareBaseScoreItem, } from "store/rs-workspace/utils/insights.utils";
import { getFieldRestrictions } from "store/knowledge-base-item/knowledge-base-item.utils";
import { InsightInputType, InsightScoreErrorType, } from "store/rs-workspace/rs-workspace.types";
import { isInsightInputSetCompleted, isSimulationVariantCompleted, } from "store/rs-workspace/utils/completeness.utils";
import { getSimulationVariantsOptions } from "store/rs-workspace/utils/results.utils";
import { RemovableEntityType } from "store/knowledge-base-item/knowledge-base-item.types";
import ChevronDownIcon from "icons/chevron_down.svg";
import DeleteIcon from "icons/delete.svg";
import * as colors from "styles/colorValues.module.scss";
import * as styles from "./styles.module.scss";
const InputSet = ({ name, restrictions }) => {
    const dispatch = useDispatch();
    const { id: workspaceId } = useParams();
    const form = useForm();
    const formState = useFormState();
    const editable = useSelector(isEditableSelector);
    const insightDefaultName = useSelector(insightDefaultNameSelector);
    const persistedInsightsData = useSelector(persistedInsightsDataSelector);
    const previewRequests = useSelector(previewRequestsSelector);
    const simulations = useSelector(workspaceSimulationsSelector);
    const cellModels = useSelector(workspaceCellModelsSelector);
    const recipes = useSelector(workspaceRecipesSelector);
    const currentInsightId = useSelector(workspaceCurrentInsightIdSelector);
    const optimizations = useSelector(workspaceOptimizationsSelector);
    const processVariables = useSelector(processVariablesSelector);
    const resultSets = useSelector(getResultSetsSelector);
    const inputOptions = useSelector(insightInputSetInputOptionsSelector);
    const expandedInputSetIndex = useSelector(insightExpandedInputSetIndexSelector);
    const deleteTarget = useSelector(deleteTargetSelector);
    const incompleteSimulationsIds = useSelector(incompleteSimulationsIdsSelector);
    const inputRestrictions = useMemo(() => getFieldRestrictions(restrictions, [
        InsightFormFields.inputSets,
        "InsightInputSet",
        InsightFormFields.inputs,
        "InsightInput",
    ]), [restrictions]);
    const inputSetId = useMemo(() => get(formState.values, name).id, [formState.values, name]);
    const inputSetExpanded = useMemo(() => {
        const inputSets = get(formState.values, InsightFormFields.inputSets);
        return inputSets.findIndex((item) => item.id === inputSetId) === expandedInputSetIndex;
    }, [inputSetId, expandedInputSetIndex, formState.values]);
    const insightScoreData = useMemo(() => getInsightLatestScoreData(persistedInsightsData, currentInsightId), [currentInsightId, persistedInsightsData]);
    const scores = useMemo(() => {
        const inputSetScores = insightScoreData?.scores.filter((score) => score.inputSetId === inputSetId);
        const filledOutputs = get(formState.values, InsightFormFields.outputs).filter((item) => item.type !== null);
        return filledOutputs.map((output) => {
            const outputScoreEntity = inputSetScores?.find((score) => score.outputId === output.id);
            const scorePending = getInsightPreviewOperationPending(previewRequests, currentInsightId, inputSetId, output.id ?? 0);
            return {
                ...prepareBaseScoreItem(outputScoreEntity?.score, checkCanDisplayScore(output, get(formState.values, name).inputs, processVariables, recipes)),
                pending: scorePending,
                highlighted: inputSetExpanded,
            };
        });
    }, [
        insightScoreData?.scores,
        formState.values,
        inputSetId,
        previewRequests,
        currentInsightId,
        name,
        processVariables,
        recipes,
        inputSetExpanded,
    ]);
    const finalScoreData = useMemo(() => {
        const finalScore = calculateInputSetFinalScore(currentInsightId === null ? -1 : currentInsightId, inputSetId, persistedInsightsData);
        const someScorePending = scores.some(({ pending }) => pending);
        const someScoreErrored = scores.some(({ errored }) => errored);
        const allScoresAvailable = scores.every((item) => isNotEmpty(item.score));
        return {
            score: allScoresAvailable ? finalScore : undefined,
            errored: someScoreErrored,
            pending: someScorePending,
            highlighted: inputSetExpanded,
            hasBiasedValue: scores.some(({ hasBiasedValue }) => hasBiasedValue),
        };
    }, [persistedInsightsData, currentInsightId, inputSetId, scores, inputSetExpanded]);
    const hasNoOptions = useMemo(() => inputOptions.length === 0, [inputOptions]);
    const inputSetUsed = useMemo(() => checkInputSetUsed(inputSetId, currentInsightId, optimizations, resultSets), [inputSetId, currentInsightId, optimizations, resultSets]);
    const hasSelectedInputs = useMemo(() => {
        const inputSetField = get(formState.values, name);
        const inputs = inputSetField.inputs;
        return inputs.some((item) => item.inputId !== null);
    }, [formState.values, name]);
    const getInputError = useCallback((idFieldName, inputIdFieldName) => {
        const id = get(formState.values, idFieldName);
        const inputEntityId = get(formState.values, inputIdFieldName);
        const outputs = get(formState.values, InsightFormFields.outputs);
        if (insightScoreData?.scores && isNotEmpty(inputEntityId)) {
            const reducedErrors = insightScoreData.scores.reduce((acc, { score, outputId }) => {
                const outputType = outputs.find(({ id }) => id === outputId)?.type;
                score.error?.errors?.forEach(({ errorType, data }) => {
                    if (data?.inputId?.some((inputId) => inputId === id)) {
                        if (errorType === InsightScoreErrorType.SIMULATION_VARIABLE_NOT_FOUND) {
                            acc.push(`Simulation doesn't have configured variable selected for ${outputType} ${outputId}.`);
                        }
                        if (errorType === InsightScoreErrorType.PROCESS_DATA_VARIABLE_NOT_FOUND) {
                            acc.push(`Process Data doesn't have configured variable selected for ${outputType} ${outputId}.`);
                        }
                    }
                });
                return acc;
            }, []);
            if (reducedErrors.length > 0) {
                return reducedErrors.join("<br/><br/>");
            }
        }
        return;
    }, [formState.values, insightScoreData?.scores]);
    const triggerInsightScoreCalculation = useCallback((updatedInputs) => {
        const insightData = getInsightDialogSubmitPayload(Object(formState.values), parseInt(workspaceId), insightDefaultName);
        const outputsToCalculate = insightData.state.outputs.filter((output) => checkBasicScoreCalculationConditions(output, processVariables, recipes));
        const untouchedInputSets = insightData.state.input.inputSets.filter((_, inputSetIndex) => inputSetIndex !== expandedInputSetIndex);
        const targetInputSet = insightData.state.input.inputSets[expandedInputSetIndex];
        const updatedInputSet = {
            ...targetInputSet,
            inputs: [
                ...targetInputSet.inputs.filter((item) => !updatedInputs.find(({ index }) => index + 1 === item.id)),
                ...updatedInputs.map((updatedInput) => {
                    const targetInput = targetInputSet.inputs.find(({ id }) => id === updatedInput.index + 1);
                    return {
                        ...targetInput,
                        inputId: updatedInput.inputId,
                        inputType: updatedInput.inputType,
                        adjustmentId: updatedInput.variantId,
                    };
                }),
            ],
        };
        const insightPayload = {
            ...insightData.state,
            input: {
                ...insightData.state.input,
                inputSets: [...untouchedInputSets, updatedInputSet],
            },
        };
        if (isInsightInputSetCompleted(updatedInputSet, insightData.state.outputs, processVariables, incompleteSimulationsIds, simulations, cellModels, recipes)) {
            outputsToCalculate.forEach((output) => {
                dispatch(getInsightScore({
                    insightId: currentInsightId,
                    insightData: {
                        outputId: output.id ?? 0,
                        inputSetId: targetInputSet.id,
                        combined: false,
                        request: insightPayload,
                    },
                }));
            });
        }
        else {
            outputsToCalculate.forEach((output) => {
                dispatch(removeInsightOutputScores({
                    insightId: currentInsightId ?? 0,
                    outputId: output.id ?? 0,
                    inputSetIds: [targetInputSet.id],
                }));
            });
        }
    }, [
        formState.values,
        workspaceId,
        insightDefaultName,
        expandedInputSetIndex,
        processVariables,
        incompleteSimulationsIds,
        dispatch,
        currentInsightId,
        simulations,
        cellModels,
        recipes,
    ]);
    const onInputChange = useCallback((option, index) => {
        const inputs = get(formState.values, `${name}.${InsightFormFields.inputs}`);
        const simulationVariants = getSimulationVariantsOptions(simulations, option.value);
        const existingProcessDataFieldWithThisValue = inputs.find((item) => item.inputId?.groupId === InsightInputType.PROCESS_DATA && item.inputId?.value === option.value);
        const indexOfAlreadySelectedField = inputs.indexOf(existingProcessDataFieldWithThisValue);
        const updatedInputs = [];
        form.batch(() => {
            if (option.groupId === InsightInputType.PROCESS_DATA) {
                form.change(`${name}.${InsightFormFields.inputs}[${index}].${InsightFormFields.adjustmentId}`, null);
            }
            if (option.groupId === InsightInputType.SIMULATION) {
                const newVariantId = simulationVariants?.length === 1 ? 0 : null;
                updatedInputs.push({
                    inputId: option.value,
                    inputType: option.groupId,
                    variantId: newVariantId,
                    index,
                });
                const alreadySelectedInputWithNewVariant = inputs.find((item) => item.inputId?.groupId === InsightInputType.SIMULATION &&
                    item.inputId?.value === option.value &&
                    item.adjustmentId === newVariantId);
                const indexOfAlreadySelectedInputField = inputs.indexOf(alreadySelectedInputWithNewVariant);
                form.change(`${name}.${InsightFormFields.inputs}[${index}].${InsightFormFields.adjustmentId}`, newVariantId);
                if (!!alreadySelectedInputWithNewVariant && indexOfAlreadySelectedInputField !== index) {
                    form.change(`${name}.${InsightFormFields.inputs}[${indexOfAlreadySelectedInputField}].${InsightFormFields.adjustmentId}`, null);
                    updatedInputs.push({
                        inputId: alreadySelectedInputWithNewVariant?.inputId?.value,
                        inputType: alreadySelectedInputWithNewVariant?.inputType,
                        variantId: null,
                        index: indexOfAlreadySelectedInputField,
                    });
                }
            }
            form.change(`${name}.${InsightFormFields.inputs}[${index}].${InsightFormFields.inputType}`, option.groupId);
            if (!!existingProcessDataFieldWithThisValue && index !== indexOfAlreadySelectedField) {
                form.change(`${name}.${InsightFormFields.inputs}[${indexOfAlreadySelectedField}].${InsightFormFields.inputId}`, null);
            }
        });
        triggerInsightScoreCalculation(updatedInputs);
    }, [form, name, formState, triggerInsightScoreCalculation, simulations]);
    const onVariantInputChange = useCallback((newVariantId, inputFieldName, inputIndex) => {
        const selectedSimulationId = get(formState.values, `${inputFieldName}.${InsightFormFields.inputId}`)?.value;
        const inputs = get(formState.values, `${name}.${InsightFormFields.inputs}`);
        const input = inputs[inputIndex];
        const alreadySelectedInputWithThisVariant = inputs.find((item) => item.inputId?.groupId === InsightInputType.SIMULATION &&
            item.inputId?.value === selectedSimulationId &&
            item.adjustmentId === newVariantId);
        const indexOfAlreadySelectedInputField = inputs.indexOf(alreadySelectedInputWithThisVariant);
        const updatedInputs = [
            {
                inputId: input?.inputId?.value,
                inputType: input?.inputType,
                variantId: newVariantId,
                index: inputIndex,
            },
        ];
        if (!!alreadySelectedInputWithThisVariant && indexOfAlreadySelectedInputField !== inputIndex) {
            form.change(`${name}.${InsightFormFields.inputs}[${indexOfAlreadySelectedInputField}].${InsightFormFields.adjustmentId}`, null);
            updatedInputs.push({
                inputId: alreadySelectedInputWithThisVariant?.inputId?.value,
                inputType: alreadySelectedInputWithThisVariant?.inputType,
                variantId: null,
                index: indexOfAlreadySelectedInputField,
            });
        }
        triggerInsightScoreCalculation(updatedInputs);
    }, [formState.values, name, form, triggerInsightScoreCalculation]);
    const onExpandInputSet = useCallback(() => {
        if (!inputSetExpanded) {
            const existingInputSets = get(formState.values, [InsightFormFields.inputSets]);
            const inputSetIndex = existingInputSets.findIndex((item) => item.id === inputSetId);
            dispatch(setInsightExpandedInputSet({ insightId: currentInsightId, inputSetIndex }));
        }
    }, [dispatch, inputSetId, currentInsightId, inputSetExpanded, formState.values]);
    const deleteInputSet = useCallback((isConfirmed) => {
        if (!isConfirmed) {
            return;
        }
        let newExpandedInputSetIndex = expandedInputSetIndex;
        const existingInputSets = get(formState.values, [InsightFormFields.inputSets]);
        const indexOfDeletedInputSet = existingInputSets.findIndex((item) => item.id === inputSetId);
        const updatedInputSets = existingInputSets?.filter((item) => item.id !== inputSetId);
        form.change(InsightFormFields.inputSets, updatedInputSets);
        if (inputSetExpanded) {
            newExpandedInputSetIndex = 0;
        }
        if (indexOfDeletedInputSet < expandedInputSetIndex) {
            --newExpandedInputSetIndex;
        }
        // TODO: once again, great place to cover with tests
        dispatch(setInsightExpandedInputSet({ insightId: currentInsightId, inputSetIndex: newExpandedInputSetIndex }));
        dispatch(setDeleteTarget(null));
    }, [inputSetId, formState.values, form, dispatch, currentInsightId, inputSetExpanded, expandedInputSetIndex]);
    const setInputSetDeleteTarget = useCallback(() => {
        dispatch(setDeleteTarget({
            id: inputSetId,
            type: RemovableEntityType.INSIGHT_INPUT_SET,
            warning: InsightLabels.INPUT_SET_USED_HINT,
        }));
    }, [dispatch, inputSetId]);
    const renderRemoveButton = useCallback(() => {
        if (!editable || get(formState.values, InsightFormFields.inputSets).length === 1) {
            return null;
        }
        if (!inputSetUsed) {
            return hasSelectedInputs ? (React.createElement(SimpleRemoveButton, { className: classNames(styles.delete, styles.padded), onOpen: setInputSetDeleteTarget, action: deleteInputSet, isMenuItem: false })) : (React.createElement(IconButton, { icon: React.createElement(DeleteIcon, null), classes: { root: styles.delete }, title: "Delete", onClick: () => deleteInputSet(true) }));
        }
        return (React.createElement(IconButton, { icon: React.createElement(DeleteIcon, null), classes: { root: styles.delete }, title: "Delete", onClick: setInputSetDeleteTarget }));
    }, [editable, formState.values, inputSetUsed, setInputSetDeleteTarget, hasSelectedInputs, deleteInputSet]);
    const renderRemoveDialog = useCallback(() => {
        if (deleteTarget === null ||
            deleteTarget.type !== RemovableEntityType.INSIGHT_INPUT_SET ||
            deleteTarget.id !== inputSetId) {
            return null;
        }
        if (inputSetUsed) {
            return (React.createElement(RemoveConfirmDialog, { title: `Delete ${deleteTarget.type}`, action: () => deleteInputSet(true), message: deleteTarget.warning ?? "" }));
        }
    }, [deleteTarget, deleteInputSet, inputSetUsed, inputSetId]);
    const getSimulationVariantIncomplete = useCallback((inputIdOption, variantId) => {
        if (inputIdOption === null || variantId === null || variantId === 0)
            return false;
        const simulation = simulations?.find(({ id }) => id === inputIdOption.value);
        const variant = simulation?.state?.adjustments?.find(({ id }) => id === variantId);
        return !isSimulationVariantCompleted(simulation, cellModels, recipes, variant);
    }, [simulations, cellModels, recipes]);
    const getInputIdExternalError = useCallback((inputIdOption) => {
        if (inputIdOption !== null && incompleteSimulationsIds.includes(inputIdOption.value)) {
            return EDIT_RS_WORKSPACE_MESSAGES.SIMULATION_IS_NOT_COMPLETED;
        }
        return undefined;
    }, [incompleteSimulationsIds]);
    const getVariantIdExternalError = useCallback((inputIdOption, variantId) => {
        if (inputIdOption !== null && incompleteSimulationsIds.includes(inputIdOption.value)) {
            return EDIT_RS_WORKSPACE_MESSAGES.SIMULATION_VARIANT_IS_NOT_COMPLETED;
        }
        if (getSimulationVariantIncomplete(inputIdOption, variantId)) {
            return EDIT_RS_WORKSPACE_MESSAGES.SIMULATION_VARIANT_IS_NOT_COMPLETED;
        }
        return undefined;
    }, [incompleteSimulationsIds, getSimulationVariantIncomplete]);
    return (React.createElement("div", { className: styles.root, "data-testid": InsightDialogTestIds.InputSet },
        React.createElement("div", { className: styles.title },
            "Input Set ",
            inputSetId,
            React.createElement("div", { className: styles.right },
                renderRemoveButton(),
                scores.map((scoreItem, index) => (React.createElement(ScoreIcon, { key: `score-${index}`, className: styles.score, testId: InsightDialogTestIds.ScoreIcon, ...scoreItem }))),
                scores.length > 1 && React.createElement("span", { className: styles.equal }, "="),
                scores.length !== 1 && (React.createElement(ScoreIcon, { className: styles.score, testId: InsightDialogTestIds.FinalScoreIcon, ...finalScoreData })),
                React.createElement(ChevronDownIcon, { className: classNames(styles.chevron, { [styles.available]: inputSetExpanded }), onClick: onExpandInputSet }))),
        React.createElement(Collapse, { keepChildrenMounted: true, isOpen: inputSetExpanded },
            React.createElement(FieldArray, { name: `${name}.${InsightFormFields.inputs}` }, ({ fields }) => fields.map((name, index) => {
                const inputError = getInputError(`${name}.${InsightFormFields.id}`, `${name}.${InsightFormFields.inputId}`);
                return (React.createElement("div", { key: name, "data-testid": InsightDialogTestIds.InputSetInput },
                    React.createElement(Input, { name: `${name}.${InsightFormFields.inputId}`, component: SelectInput, items: inputOptions, label: `Input ${index + 1}`, placeholder: hasNoOptions ? "No Simulations or Process Data in the Workspace." : "Select", itemRenderer: SelectItemRenderer, readonly: !editable, externalError: getInputIdExternalError(fields.value[index].inputId), onChange: (option) => onInputChange(option, index), restrictions: getFieldRestrictions(inputRestrictions, [InsightFormFields.inputId]), classes: {
                            container: classNames(styles.selectWrapper, {
                                [styles.errored]: isNotEmpty(inputError),
                            }),
                            component: styles.select,
                        }, componentClasses: {
                            selectButton: classNames({ [styles.withPadding]: !hasNoOptions }),
                        }, selectButtonProps: {
                            prefix: hasNoOptions ? null : (React.createElement("div", { className: classNames(styles.palette, styles.absolute), style: {
                                    backgroundColor: get(formState.values, `${name}.${InsightFormFields.inputId}`) !== null
                                        ? INSIGHT_INPUT_COLORS[index]
                                        : colors.s200,
                                } })),
                        } }),
                    React.createElement("div", { className: styles.adjustments },
                        React.createElement(Input, { name: `${name}.${InsightFormFields.adjustmentId}`, component: SelectInput, items: getInsightInputVariantsOptions(get(formState.values, `${name}.${InsightFormFields.inputType}`), simulations, cellModels, recipes, get(formState.values, `${name}.${InsightFormFields.inputId}`)?.value, get(formState.values, `${name}.${InsightFormFields.adjustmentId}`)), placeholder: "Select", valueFormatter: ({ value }) => value, itemRenderer: SelectItemRenderer, onChange: (newVariantId) => onVariantInputChange(newVariantId, name, index), restrictions: getFieldRestrictions(inputRestrictions, [InsightFormFields.adjustmentId]), classes: {
                                container: classNames(styles.selectWrapper, {
                                    [styles.errored]: isNotEmpty(inputError),
                                }),
                                component: styles.select,
                            }, readonly: !editable, externalError: getVariantIdExternalError(fields.value[index].inputId, fields.value[index].adjustmentId) })),
                    isNotEmpty(inputError) && React.createElement(HtmlText, { text: inputError, className: styles.errorMessage })));
            }))),
        renderRemoveDialog()));
};
export default InputSet;
